/**
 * A row write's answer in the console (infrastructure/db-helper/DESIGN_EDITING.md console flow 11 and "Error lines"):
 * applied, refused with one short line, or unknown, which only the settle route can answer.
 *
 * ⚠️ NO IMPORTS, ON PURPOSE (dataView.ts says why): these rules are tested on their own.
 *
 * ⛔ A WRITE IS NEVER SENT TWICE. An unknown outcome is asked about by its write_ref (settleWrite), never answered by
 *    sending the write again, and retryable() is false for every write (the view's Retry is for reads only).
 */

export type WriteOp =
    | 'insert' | 'update' | 'delete'
    | 'create_table' | 'rename_table' | 'add_column' | 'rename_column' | 'drop_column' | 'drop_table' | 'restore_table'
    | 'import_commit';

export type WriteOutcome =
    | { kind: 'applied'; affected: number | null; missing: number; tag?: string }
    | { kind: 'refused'; code: string; line: string; postgres?: string }
    | { kind: 'unknown' };

export const SAVED = 'Saved.';
export const NOT_SAVED = 'Not saved.';
export const UNKNOWN_LINE = 'Unknown. Reload.';
export const CHECKING = 'Checking...';

/** One write's id, made per click (E7): the backend refuses a second write with it (write_ref_used). */
export const newWriteRef = (): string => {
    const c: any = (globalThis as any).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const b: Uint8Array = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

// ── The lines ────────────────────────────────────────────────────────────────────────────────────────────────────

const BY_CODE: Readonly<Record<string, string>> = {
    busy: 'Table is busy. Try again.',
    name_taken: 'Name taken.',
    row_changed: 'Changed elsewhere.',
    row_not_found: 'This row is gone.',
    disk_low: 'Disk almost full.',
    import_busy: 'Another import is running.',
    data_edit_paused: 'Paused during the upgrade.',
    editing_locked: 'Editing is locked.',
    data_edit_not_installed: 'Editing is not set up on this database.',
    data_edit_disabled: 'Editing is not available for this account yet.',
    payment_suspended: 'Account on hold.',
    rate_limited: 'Too many requests. Try again in a moment.',
    write_ref_used: 'Already sent. Reload.',
};

/** Postgres's refusal of a write, by its SQLSTATE. `column` is the customer's own name, shown as text. */
export const sqlstateLine = (sqlstate: string | undefined, column: string | undefined, op: WriteOp): string => {
    switch (sqlstate) {
        case '23505': return 'Already exists.';
        case '23502': return column ? `${column} is required.` : 'A value is required.';
        case '23503': return op === 'delete' ? 'Other rows link to this one.' : 'Linked row missing.';
        case '23514': return 'Not allowed by a rule.';
        case '42501': return 'No access to this table.';
        case '57014': return 'Took too long.';
        case '2BP01': return 'Other objects depend on it.';
        case '42P07': return 'Name taken.';   // a helper before 2026-10-25 sent a taken table name as write_failed
        default:
            if (sqlstate && /^22[0-9A-Z]{3}$/.test(sqlstate)) return column ? `Wrong value for ${column}.` : 'Wrong value.';
            return NOT_SAVED;
    }
};

/** The line for a refusal: write_failed by its SQLSTATE, the rest by code, anything else "Not saved.". */
export const writeErrorLine = (code: string, details: any, op: WriteOp, onlyColumn?: string): string => {
    if (code === 'write_failed') {
        const sqlstate = typeof details?.sqlstate === 'string' ? details.sqlstate : undefined;
        // Postgres leaves the column out of most value errors (22P02); a write of one column names it anyway
        const column = typeof details?.column === 'string' && details.column ? details.column : onlyColumn;
        return sqlstateLine(sqlstate, column, op);
    }
    return Object.prototype.hasOwnProperty.call(BY_CODE, code) ? BY_CODE[code] : NOT_SAVED;
};

// ── Reading the answer ───────────────────────────────────────────────────────────────────────────────────────────

/** A 2xx: applied only as the backend words it; a 202 or any other body is unknown and is settled. */
export const readWriteSuccess = (status: number, data: any): WriteOutcome => {
    if (status === 200 && data && data.outcome === 'applied') {
        return {
            kind: 'applied',
            affected: Number.isInteger(data.affected) ? data.affected : null,
            missing: Number.isInteger(data.missing) ? data.missing : 0,
            ...(typeof data.tag === 'string' ? { tag: data.tag } : {}),
        };
    }
    return { kind: 'unknown' };
};

/**
 * A rejected write (an axios error). No answer, a timeout, a 5xx the backend did not mark applied false, or an
 * answer without the envelope: unknown, so it is settled. Everything else is a refusal: nothing was saved.
 */
export const readWriteFailure = (err: any, op: WriteOp, onlyColumn?: string): WriteOutcome => {
    const response = err?.response;
    if (!response) return { kind: 'unknown' };
    const status = Number(response.status) || 0;
    const e = response.data && typeof response.data === 'object' ? response.data.error : undefined;
    const code = e && typeof e.code === 'string' && e.code ? e.code : null;
    if (code === null) return { kind: 'unknown' };
    if (status >= 500 && e.details?.applied !== false) return { kind: 'unknown' };
    const postgres = typeof e.details?.message === 'string' && e.details.message ? e.details.message : undefined;
    return { kind: 'refused', code, line: writeErrorLine(code, e.details, op, onlyColumn), ...(postgres ? { postgres } : {}) };
};

// ── Settle (console flow 11) ─────────────────────────────────────────────────────────────────────────────────────

export const SETTLE_WAIT_MS = 5000;
/** Asks after the first: a pending answer waits SETTLE_WAIT_MS and asks again, at most this many times. */
export const SETTLE_MORE_ASKS = 3;

export const settleUrl = (databaseId: number, writeRef: string): string =>
    `/v1/databases/${databaseId}/data/writes/${encodeURIComponent(writeRef)}`;

/**
 * What became of a write whose answer was lost, asked by its write_ref. "pending" waits and asks again. A write_ref
 * the backend has no record of is claimed there as not applied (a write still on its way is then refused), so
 * not_applied is exact; any refusal of the ask itself, a 404 included, is unknown.
 */
export const settleWrite = async (
    get: (url: string) => Promise<any>, url: string, wait: (ms: number) => Promise<void>,
): Promise<'applied' | 'not_applied' | 'unknown'> => {
    for (let ask = 0; ask <= SETTLE_MORE_ASKS; ask++) {
        if (ask > 0) await wait(SETTLE_WAIT_MS);
        let data: any;
        try {
            data = await get(url);
        } catch {
            return 'unknown';
        }
        const outcome = data?.outcome;
        if (outcome === 'applied' || outcome === 'not_applied') return outcome;
        if (outcome !== 'pending') return 'unknown';
    }
    return 'unknown';
};

// ── Import's own outcome lines (console flow 9) ──────────────────────────────────────────────────────────────────

/** A count with thousands separators, in a way every browser renders the same: "1204" -> "1,204". */
const grouped = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** A finished import's success line: "Imported 1,204 rows." */
export const importedLine = (rows: number): string => `Imported ${grouped(rows)} row${rows === 1 ? '' : 's'}.`;

const IMPORT_BY_CODE: Readonly<Record<string, string>> = {
    import_bad_header: 'The file does not match the table. Nothing imported.',
    import_incomplete: 'The upload did not finish. Nothing imported.',
    chunk_mismatch: 'The upload did not match. Try again.',
    import_too_large: 'File too large.',
    disk_low: 'Disk almost full. Nothing imported.',
    import_busy: 'Another import is running.',
};

/**
 * A refused import commit, worded for console flow 9. A value error names its line and column
 * ("Line 42: wrong value for price. Nothing imported."); the rest get a short line, always ending the import.
 */
export const importFailedLine = (code: string, details: any): string => {
    if (code === 'write_failed') {
        const sqlstate = typeof details?.sqlstate === 'string' ? details.sqlstate : undefined;
        const column = typeof details?.column === 'string' && details.column ? details.column : undefined;
        const phrase = sqlstateLine(sqlstate, column, 'import_commit').replace(/\.$/, '');   // "Wrong value for price"
        const lower = phrase.charAt(0).toLowerCase() + phrase.slice(1);
        const raw = details?.line;
        const at = raw != null && /^\d+$/.test(String(raw)) ? String(raw) : null;
        return at !== null ? `Line ${at}: ${lower}. Nothing imported.` : `${phrase}. Nothing imported.`;
    }
    if (Object.prototype.hasOwnProperty.call(IMPORT_BY_CODE, code)) return IMPORT_BY_CODE[code];
    if (Object.prototype.hasOwnProperty.call(BY_CODE, code)) return BY_CODE[code];
    return 'Nothing imported.';
};

/** The line a settled write ends on. */
/** A read-only transaction refused the statement (SQLSTATE 25006): it is a write, and the console offers to run it. */
export const isReadOnlyRefusal = (detail: string | undefined): boolean =>
    /SQLSTATE 25006|read-only transaction/i.test(detail ?? '');

export const settledLine = (s: 'applied' | 'not_applied' | 'unknown'): string =>
    s === 'applied' ? SAVED : s === 'not_applied' ? NOT_SAVED : UNKNOWN_LINE;
